<template>
  <div class="app">
    <secondHeader />
    <div class="main">
      <sidebar />
      <main class="tool-content">
        <div class="bg-create-container">
          <!-- プレビューエリア -->
          <div class="preview-panel">
            <h2>背景ジェネレータ</h2>
            <div class="preview-container" ref="previewContainer">
              <div class="background-preview" :style="previewStyle">
                <div v-if="settings.patternEnabled && settings.patternType !== 'night-sky'" class="pattern-overlay" :style="patternStyle"></div>
                <div v-if="settings.patternEnabled && (settings.patternType === 'polka' || settings.patternType === 'heart')" class="pattern-overlay pattern-overlay-shifted" :style="patternShiftedStyle"></div>
                
                <!-- 星空専用レイヤー -->
                <div v-if="settings.patternEnabled && settings.patternType === 'night-sky'" class="night-sky-container">
                  <div class="stars stars-sm" :style="starsSmStyle"></div>
                  <div class="stars stars-md" :style="starsMdStyle"></div>
                  <div class="stars stars-lg" :style="starsLgStyle"></div>
                </div>
                
              </div>
            </div>
          </div>

          <div class="settings-panel">
            <h2>背景設定</h2>
            
            <!-- 背景タイプ -->
            <div class="setting-group">
              <label>背景タイプ</label>
              <div class="radio-group">
                <label class="radio-label">
                  <input type="radio" v-model="settings.bgType" value="solid" @change="onBgTypeChange">
                  単色
                </label>
                <label class="radio-label">
                  <input type="radio" v-model="settings.bgType" value="gradient" @change="onBgTypeChange">
                  グラデーション
                </label>
              </div>
            </div>

            <!-- 背景色テンプレート -->
            <div class="setting-group" v-if="settings.bgType === 'gradient'">
              <label>背景色テンプレート</label>
              <div class="template-grid">
                <div 
                  v-for="template in colorTemplates" 
                  :key="template.name"
                  class="template-item"
                  :class="{ 'active': selectedTemplate === template.name }"
                  @click="applyTemplate(template)"
                >
                  <div class="template-preview" :style="getTemplatePreviewStyle(template)"></div>
                </div>
              </div>
            </div>

            <!-- 背景色1 -->
            <div class="setting-group">
              <label>背景色1（{{ settings.bgType === 'solid' ? 'メイン' : '開始' }}）</label>
              <div class="color-picker-wrapper">
                <div class="color-preview" :style="{ backgroundColor: settings.bgColor1 }" @click.stop="showColorPicker1 = !showColorPicker1"></div>
                <input type="text" v-model="settings.bgColor1" @input="saveSettings" placeholder="#ffffff">
                <chrome-picker v-if="showColorPicker1" v-model="color1" @input="updateColor1" class="color-picker"></chrome-picker>
              </div>
            </div>

            <!-- 背景色2（グラデーション用） -->
            <div class="setting-group" v-if="settings.bgType === 'gradient'">
              <label>背景色2（終了）</label>
              <div class="color-picker-wrapper">
                <div class="color-preview" :style="{ backgroundColor: settings.bgColor2 }" @click.stop="showColorPicker2 = !showColorPicker2"></div>
                <input type="text" v-model="settings.bgColor2" @input="saveSettings" placeholder="#ffffff">
                <chrome-picker v-if="showColorPicker2" v-model="color2" @input="updateColor2" class="color-picker"></chrome-picker>
              </div>
            </div>

            <!-- グラデーション方向 -->
            <div class="setting-group" v-if="settings.bgType === 'gradient'">
              <label>グラデーション方向</label>
              <select v-model="settings.gradientDirection" @change="onGradientDirectionChange">
                <option value="to right">左→右</option>
                <option value="to bottom">上→下</option>
                <option value="to bottom right">左上→右下</option>
                <option value="135deg">斜め（135度）</option>
              </select>
            </div>

            <!-- 背景パターン -->
            <div class="setting-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="settings.patternEnabled" @change="saveSettings">
                背景パターンを有効にする
              </label>
            </div>

            <div v-if="settings.patternEnabled" class="setting-group nested">
              <label>パターンタイプ</label>
              <select v-model="settings.patternType" @change="saveSettings">
                <option value="polka">水玉</option>
                <option value="heart">ハート</option>
                <option value="stripe-vertical">ストライプ（縦）</option>
                <option value="stripe-horizontal">ストライプ（横）</option>
                <option value="stripe-diagonal">ストライプ（斜め）</option>
                <option value="check">チェック</option>
                <option value="grid">グリッド</option>
                <option value="night-sky">星空</option>
              </select>
            </div>

            <div v-if="settings.patternEnabled" class="setting-group nested">
              <label>パターンの色</label>
              <div class="color-picker-wrapper" ref="colorPickerWrapper3">
                <div class="color-preview" :style="{ backgroundColor: settings.patternColor }" @click.stop="showColorPicker3 = !showColorPicker3"></div>
                <input type="text" v-model="settings.patternColor" @input="saveSettings" placeholder="#ffffff">
                <chrome-picker v-if="showColorPicker3" v-model="color3" @input="updateColor3" class="color-picker"></chrome-picker>
              </div>
            </div>

            <div v-if="settings.patternEnabled && (settings.patternType === 'polka' || settings.patternType === 'heart')" class="setting-group nested">
              <label>{{ settings.patternType === 'polka' ? '水玉' : 'ハート' }}のサイズ (px)</label>
              <input type="range" v-model.number="settings.patternSize" min="3" max="15" @input="saveSettings">
              <span>{{ settings.patternSize }}px</span>
            </div>

            <div v-if="settings.patternEnabled && settings.patternType !== 'night-sky' && settings.patternType !== 'grid' && settings.patternType !== 'check'" class="setting-group nested">
              <label>パターンの間隔 (px)</label>
              <input type="range" v-model.number="settings.patternSpacing" min="15" max="100" @input="saveSettings">
              <span>{{ settings.patternSpacing }}px</span>
            </div>

            <div v-if="settings.patternEnabled && (settings.patternType === 'stripe-vertical' || settings.patternType === 'stripe-horizontal' || settings.patternType === 'stripe-diagonal')" class="setting-group nested">
              <label>ストライプの太さ (px)</label>
              <input type="range" v-model.number="settings.patternSize" min="1" max="20" @input="saveSettings">
              <span>{{ settings.patternSize }}px</span>
            </div>

            <div v-if="settings.patternEnabled && settings.patternType === 'check'" class="setting-group nested">
              <label>チェックのサイズ (px)</label>
              <input type="range" v-model.number="settings.patternSize" min="10" max="50" @input="saveSettings">
              <span>{{ settings.patternSize }}px</span>
            </div>

            <div v-if="settings.patternEnabled && settings.patternType === 'grid'" class="setting-group nested">
              <label>マスの大きさ (px)</label>
              <input type="range" v-model.number="settings.patternSpacing" min="10" max="200" @input="saveSettings">
              <span>{{ settings.patternSpacing }}px</span>
            </div>

            <div v-if="settings.patternEnabled && settings.patternType === 'grid'" class="setting-group nested">
              <label>線の太さ (px)</label>
              <input type="range" v-model.number="settings.patternSize" min="1" max="5" @input="saveSettings">
              <span>{{ settings.patternSize }}px</span>
            </div>

            <div v-if="settings.patternEnabled && settings.patternType === 'night-sky'" class="setting-group nested">
              <label>星の量</label>
              <input type="range" v-model.number="settings.patternSpacing" min="10" max="100" @input="saveSettings">
              <span>{{ settings.patternSpacing }}</span>
            </div>

            <div v-if="settings.patternEnabled && settings.patternType === 'night-sky'" class="setting-group nested">
              <label>星のサイズ (px)</label>
              <input type="range" v-model.number="settings.patternSize" min="1" max="10" @input="saveSettings">
              <span>{{ settings.patternSize }}px</span>
            </div>

            <div v-if="settings.patternEnabled" class="setting-group nested">
              <label>パターンの透明度</label>
              <input type="range" v-model.number="settings.patternOpacity" min="0" max="100" @input="saveSettings">
              <span>{{ settings.patternOpacity }}%</span>
            </div>

            <!-- プレビューボタン -->
            <div class="action-buttons">
              <button class="preview-button" @click="openPreview">プレビューを開く</button>
              <button class="copy-url-button" @click="copyUrl">URLをコピー</button>
            </div>

            <!-- OBS導入ガイド -->
            <div class="obs-guide">
              <h3>OBS Studioへの導入方法</h3>
              <p>URLをブラウザソースに貼り付けます。幅・高さはキャンバスサイズに合わせて設定してください。(例:1920×1080)</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { Chrome } from 'vue-color'
import secondHeader from "../components/secondHeader.vue"
import sidebar from "../components/sidebar.vue"

const HEART_SVG_PATH = "M384,46.469c-70.688,0-128,57.313-128,128.016c0-70.703-57.313-128.016-128-128.016S0,103.781,0,174.484c0,66.484,31.313,193.391,218.563,276.234c11.844,5.25,35.703,14.469,35.703,14.469c0.547,0.219,1.141,0.344,1.734,0.344s1.188-0.125,1.734-0.344c0,0,23.859-9.219,35.703-14.469C480.688,367.875,512,240.969,512,174.484C512,103.781,454.688,46.469,384,46.469z"

export default {
  name: 'BgCreate',
  components: {
    'chrome-picker': Chrome,
    secondHeader,
    sidebar
  },
  data() {
    const defaultSettings = {
      bgType: 'gradient',
      bgColor1: '#D9AFD9',
      bgColor2: '#97D9E1',
      gradientDirection: 'to bottom right',
      patternEnabled: true,
      patternType: 'polka',
      patternColor: '#ffffff',
      patternSize: 5,
      patternSpacing: 25,
      patternOpacity: 16
    }

    const savedSettings = localStorage.getItem('bgGeneratorSettings')
    const settings = savedSettings ? JSON.parse(savedSettings) : defaultSettings

    return {
      settings,
      showColorPicker1: false,
      showColorPicker2: false,
      showColorPicker3: false,
      color1: { hex: settings.bgColor1 },
      color2: { hex: settings.bgColor2 },
      color3: { hex: settings.patternColor || settings.polkaColor || '#ffffff' },
      selectedTemplate: null,
      starsSmStyle: {},
      starsMdStyle: {},
      starsLgStyle: {},
      colorTemplates: [
        {
          name: 'ラベンダー・ミント',
          bgColor1: '#D9AFD9',
          bgColor2: '#97D9E1',
          gradientDirection: 'to bottom right'
        },
        {
          name: 'ローズ・ピンク',
          bgColor1: '#FFB6C1',
          bgColor2: '#FFC0CB',
          gradientDirection: 'to bottom'
        },
        {
          name: 'サンセット',
          bgColor1: '#fad0c4',
          bgColor2: '#ffd1ff',
          gradientDirection: '135deg'
        },
        {
          name: 'スカイ・ホワイト',
          bgColor1: '#87CEEB',
          bgColor2: '#F0F8FF',
          gradientDirection: 'to bottom'
        },
        {
          name: 'ミント・ホワイト',
          bgColor1: '#B0E0E6',
          bgColor2: '#F0FFFF',
          gradientDirection: 'to bottom right'
        },
        {
          name: 'forest',
          bgColor1: '#c1dfc4',
          bgColor2: '#deecdd',
          gradientDirection: 'to bottom right'
        },
        {
          name: 'アクアスプラッシュ',
          bgColor1: '#13547a',
          bgColor2: '#80d0c7',
          gradientDirection: 'to bottom right'
        },
        {
          name: 'ピーチ・オレンジ',
          bgColor1: '#FFE4B5',
          bgColor2: '#FFDAB9',
          gradientDirection: 'to right'
        },
        {
          name: 'ブレッシング',
          bgColor1: '#d1fdff',
          bgColor2: '#fddb92',
          gradientDirection: 'to bottom'
        },
        {
          name: 'グレー',
          bgColor1: '#e2ebf0',
          bgColor2: '#cfd9df',
          gradientDirection: 'to right'
        },
        {
          name: 'パステルレインボー',
          bgColor1: '#FFB6C1',
          bgColor2: '#87CEEB',
          gradientDirection: '135deg'
        },
        {
          name: 'オーシャン（ダーク）',
          bgColor1: '#001F3F',
          bgColor2: '#003366',
          gradientDirection: 'to bottom'
        },
        {
          name: 'ラベンダー（ダーク）',
          bgColor1: '#2E0854',
          bgColor2: '#4B0082',
          gradientDirection: 'to bottom right'
        },
        {
          name: 'ダークオーシャン',
          bgColor1: '#330867',
          bgColor2: '#30cfd0',
          gradientDirection: 'to bottom'
        },
      ]
    }
  },
  computed: {
    previewStyle() {
      let style = {}
      
      if (this.settings.bgType === 'gradient') {
        style.background = `linear-gradient(${this.settings.gradientDirection}, ${this.settings.bgColor1}, ${this.settings.bgColor2})`
      } else {
        style.backgroundColor = this.settings.bgColor1
      }
      
      return style
    },
    patternStyle() {
      const opacity = this.settings.patternOpacity / 100
      const color = this.hexToRgba(this.settings.patternColor, opacity)
      let backgroundImage = ''
      let backgroundSize = ''
      let backgroundPosition = '0 0'
      
      switch (this.settings.patternType) {
        case 'polka':
          backgroundImage = `radial-gradient(circle, ${color} ${this.settings.patternSize / 2}px, transparent ${this.settings.patternSize / 2}px)`
          backgroundSize = `${this.settings.patternSpacing}px ${this.settings.patternSpacing}px`
          break
        case 'heart': {
          const heartSvgUrl = this.generateHeartSvg(opacity)
          backgroundImage = `url("${heartSvgUrl}")`
          backgroundSize = `${this.settings.patternSpacing}px ${this.settings.patternSpacing}px`
          break
        }
        case 'stripe-vertical':
          backgroundImage = `repeating-linear-gradient(to right, transparent, transparent ${this.settings.patternSpacing - this.settings.patternSize}px, ${color} ${this.settings.patternSpacing - this.settings.patternSize}px, ${color} ${this.settings.patternSpacing}px)`
          backgroundSize = `${this.settings.patternSpacing}px 100%`
          break
        case 'stripe-horizontal':
          backgroundImage = `repeating-linear-gradient(to bottom, transparent, transparent ${this.settings.patternSpacing - this.settings.patternSize}px, ${color} ${this.settings.patternSpacing - this.settings.patternSize}px, ${color} ${this.settings.patternSpacing}px)`
          backgroundSize = `100% ${this.settings.patternSpacing}px`
          break
        case 'stripe-diagonal':
          backgroundImage = `repeating-linear-gradient(45deg, ${color}, ${color} ${this.settings.patternSize}px, transparent ${this.settings.patternSize}px, transparent ${this.settings.patternSpacing}px)`
          backgroundSize = 'auto'
          break
        case 'check': {
          const checkSize = this.settings.patternSize
          const checkColor = color
          backgroundImage = `
            linear-gradient(${checkColor} ${checkSize}px, transparent ${checkSize}px),
            linear-gradient(90deg, ${checkColor} ${checkSize}px, transparent ${checkSize}px)
          `
          backgroundSize = `${checkSize * 2}px ${checkSize * 2}px`
          break
        }
        case 'grid': {
          const gridSize = this.settings.patternSpacing
          const lineWidth = this.settings.patternSize
          const gridColor = color
          backgroundImage = `
            linear-gradient(${gridColor} ${lineWidth}px, transparent ${lineWidth}px),
            linear-gradient(90deg, ${gridColor} ${lineWidth}px, transparent ${lineWidth}px)
          `
          backgroundSize = `${gridSize}px ${gridSize}px`
          break
        }
        case 'night-sky':
          // night-skyは特別な処理が必要なのでここでは何もしない
          break
      }
      
      if (this.settings.patternType === 'night-sky') {
        return {}
      }
      
      return {
        backgroundImage: backgroundImage.trim(),
        backgroundSize: backgroundSize,
        backgroundPosition: backgroundPosition,
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
      }
    },
    patternShiftedStyle() {
      if (this.settings.patternType === 'night-sky') {
        return {}
      }

      if (this.settings.patternType !== 'polka' && this.settings.patternType !== 'heart') return {}
      
      const opacity = this.settings.patternOpacity / 100
      const color = this.hexToRgba(this.settings.patternColor, opacity)
      const shift = this.settings.patternSpacing / 2
      
      if (this.settings.patternType === 'polka') {
        return {
          backgroundImage: `radial-gradient(circle, ${color} ${this.settings.patternSize / 2}px, transparent ${this.settings.patternSize / 2}px)`,
          backgroundSize: `${this.settings.patternSpacing}px ${this.settings.patternSpacing}px`,
          backgroundPosition: `${shift}px ${shift}px`,
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none'
        }
      }
      
      if (this.settings.patternType === 'heart') {
        const heartSvgUrl = this.generateHeartSvg(opacity)
        return {
          backgroundImage: `url("${heartSvgUrl}")`,
          backgroundSize: `${this.settings.patternSpacing}px ${this.settings.patternSpacing}px`,
          backgroundPosition: `${shift}px ${shift}px`,
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none'
        }
      }
      
      return {}
    }
  },
  mounted() {
    this.loadSettings()
    if (this.settings.patternType === 'night-sky') {
      this.generateNightSky()
    }
    // カラーピッカーの外側クリックで閉じる
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  watch: {
    'settings.patternType': function(newVal) {
      if (newVal === 'night-sky') {
        this.generateNightSky()
      }
    },
    'settings.patternSpacing': function() {
      if (this.settings.patternType === 'night-sky') {
        this.generateNightSky()
      }
    },
    'settings.patternColor': function() {
      if (this.settings.patternType === 'night-sky') {
        this.generateNightSky()
      }
    },
    'settings.patternOpacity': function() {
      if (this.settings.patternType === 'night-sky') {
        this.generateNightSky()
      }
    },
    'settings.patternSize': function() {
      if (this.settings.patternType === 'night-sky') {
        this.generateNightSky()
      }
    }
  },
  methods: {
    generateNightSky() {
      const density = Math.max(10, this.settings.patternSpacing) 
      const baseCount = density * 3 
      
      const baseColor = this.settings.patternColor
      const opacity = this.settings.patternOpacity / 100
      const baseSize = Math.max(1, this.settings.patternSize / 5)

      this.starsSmStyle = this.createStarLayer(baseCount * 3, 1 * baseSize, baseColor, opacity * 0.8)
      this.starsMdStyle = this.createStarLayer(baseCount * 1.5, 2 * baseSize, baseColor, opacity)
      this.starsLgStyle = this.createStarLayer(baseCount * 0.5, 3 * baseSize, baseColor, opacity)
    },
    createStarLayer(count, size, hexColor, opacity) {
      let boxShadow = ''
      const r = parseInt(hexColor.slice(1, 3), 16)
      const g = parseInt(hexColor.slice(3, 5), 16)
      const b = parseInt(hexColor.slice(5, 7), 16)
      
      for (let i = 0; i < count; i++) {
        const alpha = (Math.random() * 0.5 + 0.5) * opacity 
        const color = `rgba(${r}, ${g}, ${b}, ${alpha})`
        const blur = size > 1.5 ? Math.random() * 2 + 1 : 0
        boxShadow += `${Math.random() * 100}vw ${Math.random() * 100}vh ${blur}px ${size/2}px ${color}, `
      }
      
      boxShadow = boxShadow.slice(0, -2)
      
      return {
        boxShadow: boxShadow,
        width: '1px',
        height: '1px',
        background: 'transparent',
        position: 'absolute',
        opacity: 1
      }
    },
    updateColor1(color) {
      this.settings.bgColor1 = color.hex
      this.color1 = color
      this.selectedTemplate = null // 手動で色を変更したらテンプレート選択を解除
      this.saveSettings()
    },
    updateColor2(color) {
      this.settings.bgColor2 = color.hex
      this.color2 = color
      this.selectedTemplate = null // 手動で色を変更したらテンプレート選択を解除
      this.saveSettings()
    },
    updateColor3(color) {
      this.settings.patternColor = color.hex
      this.color3 = color
      this.saveSettings()
    },
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    saveSettings() {
      // URLパラメータベースに変更したため、localStorageへの保存は不要
      // 必要に応じて後方互換性のために残すことも可能
    },
    loadSettings() {
      // URLパラメータから設定を読み込む
      const params = new URLSearchParams(window.location.search)
      if (params.has('bgType')) {
        this.settings.bgType = params.get('bgType') || this.settings.bgType
        this.settings.bgColor1 = params.get('bgColor1') || this.settings.bgColor1
        this.settings.bgColor2 = params.get('bgColor2') || this.settings.bgColor2
        this.settings.gradientDirection = params.get('gradientDirection') || this.settings.gradientDirection
        this.settings.patternEnabled = params.get('patternEnabled') === 'true'
        this.settings.patternType = params.get('patternType') || this.settings.patternType
        this.settings.patternColor = params.get('patternColor') || this.settings.patternColor
        const patternSizeParam = parseInt(params.get('patternSize'))
        this.settings.patternSize = !isNaN(patternSizeParam) ? patternSizeParam : this.settings.patternSize
        const patternSpacingParam = parseInt(params.get('patternSpacing'))
        this.settings.patternSpacing = !isNaN(patternSpacingParam) ? patternSpacingParam : this.settings.patternSpacing
        const patternOpacityParam = parseInt(params.get('patternOpacity'))
        this.settings.patternOpacity = !isNaN(patternOpacityParam) ? patternOpacityParam : this.settings.patternOpacity
        
        this.color1 = { hex: this.settings.bgColor1 }
        this.color2 = { hex: this.settings.bgColor2 }
        this.color3 = { hex: this.settings.patternColor }
        this.checkCurrentTemplate()
        
        if (this.settings.patternType === 'night-sky') {
          this.generateNightSky()
        }
      } else {
        // URLパラメータがない場合は、localStorageから読み込む（後方互換性）
        const saved = localStorage.getItem('bgGeneratorSettings')
        if (saved) {
          try {
            this.settings = JSON.parse(saved)
            // 後方互換性のため、古い設定名を新しい設定名に変換
            if (this.settings.polkaEnabled !== undefined && this.settings.patternEnabled === undefined) {
              this.settings.patternEnabled = this.settings.polkaEnabled
              this.settings.patternType = 'polka'
              this.settings.patternColor = this.settings.polkaColor || '#ffffff'
              this.settings.patternSize = this.settings.polkaSize || 5
              this.settings.patternSpacing = this.settings.polkaSpacing || 25
              this.settings.patternOpacity = this.settings.polkaOpacity || 16
            }
            this.color1 = { hex: this.settings.bgColor1 }
            this.color2 = { hex: this.settings.bgColor2 }
            this.color3 = { hex: this.settings.patternColor || this.settings.polkaColor || '#ffffff' }
            this.checkCurrentTemplate()
            
            if (this.settings.patternType === 'night-sky') {
              this.generateNightSky()
            }
          } catch (e) {
            console.error('設定の読み込みに失敗しました', e)
          }
        }
      }
    },
    checkCurrentTemplate() {
      // 現在の設定に一致するテンプレートを検出
      const matched = this.colorTemplates.find(template => 
        template.bgColor1 === this.settings.bgColor1 &&
        template.bgColor2 === this.settings.bgColor2 &&
        template.gradientDirection === this.settings.gradientDirection
      )
      this.selectedTemplate = matched ? matched.name : null
    },
    generatePreviewUrl() {
      const params = new URLSearchParams()
      params.set('bgType', this.settings.bgType)
      params.set('bgColor1', this.settings.bgColor1)
      params.set('bgColor2', this.settings.bgColor2)
      params.set('gradientDirection', this.settings.gradientDirection)
      params.set('patternEnabled', this.settings.patternEnabled.toString())
      params.set('patternType', this.settings.patternType)
      params.set('patternColor', this.settings.patternColor)
      params.set('patternSize', this.settings.patternSize.toString())
      params.set('patternSpacing', this.settings.patternSpacing.toString())
      params.set('patternOpacity', this.settings.patternOpacity.toString())
      
      const baseUrl = window.location.origin + '/tool/bgdesign'
      return `${baseUrl}?${params.toString()}`
    },
    openPreview() {
      const url = this.generatePreviewUrl()
      window.open(url, '_blank')
    },
    async copyUrl() {
      const url = this.generatePreviewUrl()
      try {
        await navigator.clipboard.writeText(url)
        alert('URLをクリップボードにコピーしました')
      } catch (err) {
        // フォールバック: テキストエリアを使用
        const textarea = document.createElement('textarea')
        textarea.value = url
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        try {
          document.execCommand('copy')
          alert('URLをクリップボードにコピーしました')
        } catch (e) {
          alert('URLのコピーに失敗しました。手動でコピーしてください:\n' + url)
        }
        document.body.removeChild(textarea)
      }
    },
    applyTemplate(template) {
      this.settings.bgColor1 = template.bgColor1
      this.settings.bgColor2 = template.bgColor2
      this.settings.gradientDirection = template.gradientDirection
      this.color1 = { hex: template.bgColor1 }
      this.color2 = { hex: template.bgColor2 }
      this.selectedTemplate = template.name
      this.saveSettings()
    },
    getTemplatePreviewStyle(template) {
      return {
        background: `linear-gradient(${template.gradientDirection}, ${template.bgColor1}, ${template.bgColor2})`
      }
    },
    onGradientDirectionChange() {
      this.selectedTemplate = null // 手動で方向を変更したらテンプレート選択を解除
      this.saveSettings()
    },
    onBgTypeChange() {
      this.selectedTemplate = null // 背景タイプを変更したらテンプレート選択を解除
      this.saveSettings()
    },
    handleClickOutside(event) {
      // カラーピッカー1の外側クリック
      if (this.showColorPicker1 && !event.target.closest('.color-picker-wrapper')) {
        this.showColorPicker1 = false
      }
      // カラーピッカー2の外側クリック
      if (this.showColorPicker2 && !event.target.closest('.color-picker-wrapper')) {
        this.showColorPicker2 = false
      }
      // カラーピッカー3の外側クリック
      if (this.showColorPicker3 && !event.target.closest('.color-picker-wrapper')) {
        this.showColorPicker3 = false
      }
    },
    generateHeartSvg(fillOpacity) {
      const spacing = Math.max(1, this.settings.patternSpacing)
      const heartScale = this.getHeartScale()
      const transform = `translate(256 256) scale(${heartScale}) translate(-256 -256)`
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${spacing}' height='${spacing}' viewBox='0 0 512 512'><path d='${HEART_SVG_PATH}' fill='${this.settings.patternColor}' fill-opacity='${fillOpacity}' transform='${transform}'/></svg>`
      return `data:image/svg+xml,${encodeURIComponent(svg)}`
    },
    getHeartScale() {
      const spacing = Math.max(1, this.settings.patternSpacing)
      const normalized = this.settings.patternSize / spacing
      return Math.min(1.1, Math.max(0.2, normalized * 2))
    },
    generateLensFlareSvg(fillOpacity) {
      const spacing = Math.max(20, this.settings.patternSpacing)
      const centerX = spacing / 2
      const centerY = spacing / 2
      const flareSize = Math.max(3, Math.min(this.settings.patternSize * 2, spacing * 0.4))
      const hexColor = this.settings.patternColor
      
      // RGB値を取得
      const r = parseInt(hexColor.slice(1, 3), 16)
      const g = parseInt(hexColor.slice(3, 5), 16)
      const b = parseInt(hexColor.slice(5, 7), 16)
      
      // 中心の明るいコア
      const coreSize = flareSize * 0.3
      const coreColor = `rgba(${r}, ${g}, ${b}, ${fillOpacity * 0.9})`
      
      // 外側のリング（より透明）
      const ring1Size = flareSize * 0.6
      const ring1Color = `rgba(${r}, ${g}, ${b}, ${fillOpacity * 0.4})`
      const ring2Size = flareSize * 0.9
      const ring2Color = `rgba(${r}, ${g}, ${b}, ${fillOpacity * 0.2})`
      
      // 放射状の光の筋（ストリーク）
      const streakCount = 8
      const streakLength = flareSize * 1.2
      const streakWidth = Math.max(1, flareSize * 0.1)
      const streakColor = `rgba(${r}, ${g}, ${b}, ${fillOpacity * 0.3})`
      
      let streaks = ''
      for (let i = 0; i < streakCount; i++) {
        const angle = (i * 360 / streakCount) * Math.PI / 180
        const x1 = centerX + Math.cos(angle) * (coreSize + streakWidth)
        const y1 = centerY + Math.sin(angle) * (coreSize + streakWidth)
        const x2 = centerX + Math.cos(angle) * streakLength
        const y2 = centerY + Math.sin(angle) * streakLength
        streaks += `<line x1='${x1}' y1='${y1}' x2='${x2}' y2='${y2}' stroke='${streakColor}' stroke-width='${streakWidth}' stroke-linecap='round' opacity='${fillOpacity}'/>`
      }
      
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${spacing}' height='${spacing}' viewBox='0 0 ${spacing} ${spacing}'>
        <!-- 外側のリング -->
        <circle cx='${centerX}' cy='${centerY}' r='${ring2Size}' fill='${ring2Color}'/>
        <!-- 中間のリング -->
        <circle cx='${centerX}' cy='${centerY}' r='${ring1Size}' fill='${ring1Color}'/>
        <!-- 放射状の光の筋 -->
        ${streaks}
        <!-- 中心のコア -->
        <circle cx='${centerX}' cy='${centerY}' r='${coreSize}' fill='${coreColor}'/>
      </svg>`
      
      return `data:image/svg+xml,${encodeURIComponent(svg)}`
    },
  }
}
</script>

<style scoped>
/* Layout */
.app {
  min-height: 100vh;
}

.main {
  display: flex;
  min-height: 100vh;
}

.tool-content {
  flex: 1;
  background: #fafafa;
  padding: 0 16px;
  overflow-x: hidden;
}

.bg-create-container {
  display: flex;
  gap: 30px;
  padding: 32px 0;
}

.settings-panel {
  flex: 1;
  background: white;
  padding: 25px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.settings-panel h2 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #4c5c8a;
  border-bottom: 2px solid #4c5c8a;
  padding-bottom: 10px;
}

.setting-group {
  margin-bottom: 20px;
}

.setting-group.nested {
  margin-left: 20px;
  margin-top: 10px;
  padding-left: 15px;
  border-left: 3px solid #e0e0e0;
}

.setting-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #555;
  font-size: 0.9rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.radio-group {
  display: flex;
  gap: 20px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
}

.radio-label input[type="radio"] {
  cursor: pointer;
}

.color-picker-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
}

.color-preview {
  width: 40px;
  height: 40px;
  border: 2px solid #ddd;
  border-radius: 5px;
  cursor: pointer;
}

.color-picker-wrapper input[type="text"] {
  flex: 1;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 0.9rem;
}

.color-picker {
  position: absolute;
  top: 50px;
  left: 0;
  z-index: 1000;
}

.setting-group select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 0.9rem;
}

.setting-group input[type="range"] {
  width: 70%;
  margin-right: 10px;
}

.setting-group span {
  color: #666;
  font-size: 0.9rem;
}

.template-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 12px;
  margin-top: 10px;
}

.template-item {
  cursor: pointer;
  padding: 4px;
  border: 2px solid #ddd;
  border-radius: 8px;
  transition: all 0.2s;
  background: white;
}

.template-item:hover {
  border-color: #4c5c8a;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.template-item.active {
  border-color: #4c5c8a;
  box-shadow: 0 2px 8px rgba(76, 92, 138, 0.3);
}

.template-preview {
  width: 100%;
  height: 30px;
  border-radius: 4px;
}

.action-buttons {
  margin-top: 30px;
  display: flex;
  gap: 10px;
}

.preview-button,
.copy-url-button {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 5px;
  font-size: 1rem;
  cursor: pointer;
  font-weight: bold;
}

.preview-button {
  background-color: #4c5c8a;
  color: white;
}

.preview-button:hover {
  background-color: #3b4b74;
}

.copy-url-button {
  background-color: #5cb85c;
  color: white;
}

.copy-url-button:hover {
  background-color: #4cae4c;
}

.obs-guide {
  margin-top: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.obs-guide h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #4c5c8a;
  font-size: 1.1rem;
}

.preview-panel {
  flex: 1;
  padding: 25px;
  border-radius: 10px;
}

.preview-panel h2 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #4c5c8a;
  border-bottom: 2px solid #4c5c8a;
  padding-bottom: 10px;
}

.preview-container {
  width: 100%;
  aspect-ratio: 16 / 9;
  border: 2px solid #fff;
  border-radius: 5px;
  overflow: hidden;
  background: white;
}

.background-preview {
  width: 100%;
  height: 100%;
  position: relative;
}

@keyframes animStar {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-100vh);
  }
}

/* 星空のスタイル */
.night-sky-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  perspective: 1000px;
}

.stars {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: block;
}

.stars-sm {
  animation: animStar 150s linear infinite;
}
.stars-sm:after {
  content: " ";
  position: absolute;
  top: 100vh;
  width: 1px;
  height: 1px;
  background: transparent;
  box-shadow: inherit;
}

.stars-md {
  animation: animStar 100s linear infinite;
}
.stars-md:after {
  content: " ";
  position: absolute;
  top: 100vh;
  width: 1px;
  height: 1px;
  background: transparent;
  box-shadow: inherit;
}

.stars-lg {
  animation: animStar 50s linear infinite;
}
.stars-lg:after {
  content: " ";
  position: absolute;
  top: 100vh;
  width: 1px;
  height: 1px;
  background: transparent;
  box-shadow: inherit;
}

@keyframes twinkle {
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}

@media (max-width: 1024px) {
  .bg-create-container {
    flex-direction: column;
  }
  
  .settings-panel {
    max-height: none;
  }
  
  .tool-content {
    padding: 0 8px;
  }
  
  .bg-create-container {
    padding: 24px 0;
  }
}

@media (max-width: 768px) {
  .bg-create-container {
    padding: 16px 0;
    gap: 20px;
  }
  
  .settings-panel {
    padding: 20px;
  }
  
  .preview-panel {
    padding: 20px;
  }
}
</style>

